import youtubeLogo from "../assets/youtube.jpg";
import ctvLogo from "../assets/ctv.jpg";
import displayLogo from "../assets/display.jpg";
import doohLogo from "../assets/dooh.gif";
import googleLogo from "../assets/google.jpg";
import openaiLogo from "../assets/openAI.png";
import platformFallbackLogo from "../assets/aioptimizations.gif";
import metaLogo from "../assets/meta.jpg";
import facebookLogo from "../assets/facebook.jpg";
import instagramLogo from "../assets/instagram.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import xLogo from "../assets/x.jpg";
import rumbleLogo from "../assets/rumble-logo.jpg";
import truthSocialLogo from "../assets/truth-social-logo.jpg";
import amazonLogo from "../assets/amazon.jpg";
import yahooLogo from "../assets/yahoo.png";
import viantLogo from "../assets/viant.png";
import { normalizeProviderSurfaceKey } from "./providerSurfaceRegistry";
import shopifyLogo from "../assets/shopify.png";
import linkedinLogo from "../assets/linkedin.png";
import redditLogo from "../assets/reddit.jpg";
import googleAnalyticsLogo from "../assets/googleAnalytics.jpg";
import stackadaptLogo from "../assets/stackadapt.jpg";
import metricoolLogo from "../assets/metricool.jpg";
import videoLogo from "../assets/video.jpg";
import audioLogo from "../assets/audio.jpg";
import mobileLogo from "../assets/mobile.jpg";
import ingameLogo from "../assets/ingame.jpg";
import emailLogo from "../assets/email.jpg";
import platformHeroAmazon from "../assets/platform-heroes/platform-hero-amazon.jpg";
import platformHeroCtv from "../assets/platform-heroes/platform-hero-ctv.jpg";
import platformHeroDisplay from "../assets/platform-heroes/platform-hero-display.jpg";
import platformHeroDooh from "../assets/platform-heroes/platform-hero-dooh.jpg";
import platformHeroMeta from "../assets/platform-heroes/platform-hero-meta.jpg";
import platformHeroProgrammatic from "../assets/platform-heroes/platform-hero-programmatic.jpg";
import platformHeroTikTok from "../assets/platform-heroes/platform-hero-tiktok.jpg";
import platformHeroTruthSocial from "../assets/platform-heroes/platform-hero-truth-social.jpg";
import platformHeroUnknown from "../assets/platform-heroes/platform-hero-unknown.jpg";
import platformHeroX from "../assets/platform-heroes/platform-hero-x.jpg";

export const PLATFORM_BRANDING = {
  youtube: { key: "youtube", label: "YouTube", image: youtubeLogo },
  // Canonical display name for the ctv identity is exactly "CTV". The legacy
  // "CTV / OTT" phrasing is retired from presentation everywhere (Sherloc and
  // the strategy microsite already display-normalized it; Platform Analytics
  // reads this label verbatim, so it has to be right here). Input aliases are
  // unaffected — resolvePlatformBrandKey still folds "OTT", "CTV / OTT" and
  // "Connected TV" onto the same "ctv" key.
  ctv: { key: "ctv", label: "CTV", image: ctvLogo },
  display: { key: "display", label: "Display", image: displayLogo },
  dooh: { key: "dooh", label: "DOOH", image: doohLogo },
  google_search: { key: "google_search", label: "Google Search", image: googleLogo },
  chatgpt_search: { key: "chatgpt_search", label: "ChatGPT Search", image: openaiLogo },
  meta: { key: "meta", label: "Meta", image: metaLogo },
  facebook: { key: "facebook", label: "Facebook", image: facebookLogo },
  instagram: { key: "instagram", label: "Instagram", image: instagramLogo },
  tiktok: { key: "tiktok", label: "TikTok", image: tiktokLogo },
  x: { key: "x", label: "X", image: xLogo },
  rumble: { key: "rumble", label: "Rumble", image: rumbleLogo },
  truth_social: { key: "truth_social", label: "Truth Social", image: truthSocialLogo },
  amazon: { key: "amazon", label: "Amazon", image: amazonLogo },
  yahoo: { key: "yahoo", label: "Yahoo!", image: yahooLogo },
  viant: { key: "viant", label: "Viant", image: viantLogo },
  shopify: { key: "shopify", label: "Shopify", image: shopifyLogo },
  linkedin: { key: "linkedin", label: "LinkedIn", image: linkedinLogo },
  video: { key: "video", label: "Video", image: videoLogo },
  audio: { key: "audio", label: "Audio", image: audioLogo },
  mobile: { key: "mobile", label: "Mobile", image: mobileLogo },
  ingame: { key: "ingame", label: "In-Game", image: ingameLogo },
  email: { key: "email", label: "Email", image: emailLogo },
};

/*
 * Canonical categorical platform palette.
 *
 * These hex values mirror the cross-platform reporting contract (Quantum
 * src/crossPlatform/crossPlatformContract.js), the one validated categorical
 * palette for platform series. Rollup families carry their family color: the
 * Meta family (meta/facebook/instagram) shares the Meta color, Google Search
 * shares the Google Ads color, and the programmatic channels
 * (display/video/audio/mobile/ingame/dooh) share the Programmatic color.
 * Platforms outside the canonical categorical set carry no color on purpose:
 * consumers keep their governed fallback rather than inheriting an invented
 * hex, and the contract's "other" gray is never painted onto a named platform
 * because it encodes "unmapped", not a brand.
 */
export const PLATFORM_CANONICAL_COLORS = Object.freeze({
  meta: "#7c6cf0",
  facebook: "#7c6cf0",
  instagram: "#7c6cf0",
  google_search: "#d96540",
  youtube: "#1e97cc",
  tiktok: "#d8409f",
  ctv: "#1da36f",
  display: "#4f7df0",
  video: "#4f7df0",
  audio: "#4f7df0",
  mobile: "#4f7df0",
  ingame: "#4f7df0",
  dooh: "#4f7df0",
  amazon: "#b5820f",
  // BR-2 (Phase 4 review, 2026-09-13): Yahoo! DSP and Viant are canonical reporting
  // channels; the cross-platform roster names them, so the branding mirror must carry
  // the same hex. OWNER DECISION: placeholder values pending the categorical palette.
  yahoo: "#7b3fe4",
  viant: "#c2410c",
});

// Applied at initialization rather than inline per entry: the entry literals
// above are source-text pinned by suites in both repositories, and the
// palette stays one legible unit beside its source-of-truth note.
Object.entries(PLATFORM_CANONICAL_COLORS).forEach(([key, color]) => {
  PLATFORM_BRANDING[key].color = color;
});

export const PLATFORM_FALLBACK_BRANDING = {
  key: "platform",
  label: "Platform",
  image: platformFallbackLogo,
};

/*
 * Card-sized derivatives of the governed repository artwork. The resolver
 * below is the single Platform Analytics hero-image contract: it shares the
 * established identity normalization above, adds only explicit rollup aliases,
 * and always returns a bundled local asset.
 */
export const PLATFORM_HERO_IMAGES = Object.freeze({
  meta: platformHeroMeta,
  facebook: facebookLogo,
  instagram: instagramLogo,
  audience_network: platformHeroMeta,
  messenger: platformHeroMeta,
  google_ads: googleLogo,
  google_search: googleLogo,
  chatgpt_search: openaiLogo,
  youtube: youtubeLogo,
  tiktok: platformHeroTikTok,
  ctv: platformHeroCtv,
  programmatic: platformHeroProgrammatic,
  amazon_ads: platformHeroAmazon,
  yahoo: yahooLogo,
  viant: viantLogo,
  x: platformHeroX,
  rumble: rumbleLogo,
  truth_social: platformHeroTruthSocial,
  // Connection-surface identities. These platforms have brand artwork but no
  // Platform Analytics rollup of their own, so they resolve to their logo
  // rather than to the unknown placeholder.
  reddit: redditLogo,
  google_analytics: googleAnalyticsLogo,
  linkedin: linkedinLogo,
  shopify: shopifyLogo,
  stackadapt: stackadaptLogo,
  metricool: metricoolLogo,
  display: platformHeroDisplay,
  audio: audioLogo,
  dooh: platformHeroDooh,
  mobile: mobileLogo,
  video: videoLogo,
  ingame: ingameLogo,
  email: emailLogo,
  unknown: platformHeroUnknown,
});

const normalizePlatformBrandInput = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const normalizePlatformHeroKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const PLATFORM_HERO_KEY_ALIASES = Object.freeze({
  audience_network: "audience_network",
  audiencenetwork: "audience_network",
  messenger: "messenger",
  google_ads: "google_ads",
  googleads: "google_ads",
  google: "google_ads",
  google_search: "google_search",
  googlesearch: "google_search",
  programmatic: "programmatic",
  amazon: "amazon_ads",
  amazon_ads: "amazon_ads",
  amazonads: "amazon_ads",
  ga4: "google_analytics",
  googleanalytics: "google_analytics",
  google_analytics: "google_analytics",
  other: "unknown",
  platform: "unknown",
  unknown: "unknown",
});

const getPlatformBrandInputCandidates = (platform) => {
  if (platform && typeof platform === "object") {
    return [
      platform.key,
      platform.label,
      platform.title,
      platform.platform,
      platform.platformName,
      platform.name,
      platform.channel,
    ].filter((value) => value !== undefined && value !== null && String(value).trim());
  }
  return [platform].filter((value) => value !== undefined && value !== null && String(value).trim());
};

export const resolvePlatformBrandKey = (value) => {
  const providerKey = normalizeProviderSurfaceKey(value);
  if (providerKey) return providerKey === "yahoo_dsp" ? "yahoo" : providerKey;
  const normalized = normalizePlatformBrandInput(value);
  if (!normalized) return "";
  const hasFacebook = normalized.includes("facebook");
  const hasInstagram = normalized.includes("instagram");
  if (
    normalized === "x" ||
    normalized === "twitter" ||
    normalized === "xads" ||
    normalized === "twitterads" ||
    normalized === "twitterx" ||
    normalized === "xtwitter" ||
    normalized.includes("twitter")
  ) {
    return "x";
  }
  if (normalized.includes("rumble")) return "rumble";
  if (normalized.includes("truthsocial")) return "truth_social";
  if (normalized.includes("amazon")) return "amazon";
  if (normalized === "yahoo" || normalized === "yahoodsp") return "yahoo";
  if (normalized.includes("shopify")) return "shopify";
  if (normalized.includes("linkedin")) return "linkedin";
  if (normalized.includes("youtube")) return "youtube";
  if (
    normalized === "connectedtv" ||
    normalized.includes("ctv") ||
    normalized.includes("ott")
  ) return "ctv";
  if (normalized === "mobile" || normalized.includes("mobile") || normalized.includes("inapp")) {
    return "mobile";
  }
  if (normalized.includes("display") || normalized.includes("banner")) return "display";
  if (
    normalized.includes("dooh") ||
    normalized.includes("digitaloutofhome") ||
    normalized.includes("outofhome") ||
    normalized.includes("ooh")
  ) {
    return "dooh";
  }
  if (
    normalized.includes("chatgpt") ||
    normalized.includes("openai")
  ) {
    return "chatgpt_search";
  }
  if (
    normalized.includes("googlesearch") ||
    normalized.includes("googlepaidsearch") ||
    normalized.includes("paidsearch") ||
    normalized === "google" ||
    normalized === "search"
  ) {
    return "google_search";
  }
  if (
    normalized === "meta" ||
    normalized === "facebookinstagram" ||
    normalized === "facebookandinstagram" ||
    (normalized.startsWith("meta") && hasFacebook && hasInstagram)
  ) {
    return "meta";
  }
  if (hasFacebook) return "facebook";
  if (hasInstagram) return "instagram";
  if (normalized.includes("tiktok")) return "tiktok";
  if (
    normalized === "video" ||
    normalized === "olv" ||
    normalized.includes("onlinevideo") ||
    normalized.includes("programmaticvideo") ||
    normalized.includes("videoolv")
  ) {
    return "video";
  }
  if (
    normalized === "audio" ||
    normalized.includes("digitalaudio") ||
    normalized.includes("streamingaudio") ||
    normalized.includes("podcast")
  ) {
    return "audio";
  }
  if (normalized.includes("email")) return "email";
  return normalized;
};

export const resolvePlatformHeroKey = (platform) => {
  const candidates = getPlatformBrandInputCandidates(platform);
  for (const candidate of candidates) {
    const exactKey = normalizePlatformHeroKey(candidate);
    const aliasedKey = PLATFORM_HERO_KEY_ALIASES[exactKey];
    if (aliasedKey && PLATFORM_HERO_IMAGES[aliasedKey]) return aliasedKey;

    const brandKey = resolvePlatformBrandKey(candidate);
    const heroKey = PLATFORM_HERO_KEY_ALIASES[brandKey] || brandKey;
    if (PLATFORM_HERO_IMAGES[heroKey]) return heroKey;
  }
  return "unknown";
};

export const resolvePlatformHeroImage = (platform) =>
  PLATFORM_HERO_IMAGES[resolvePlatformHeroKey(platform)] ||
  PLATFORM_HERO_IMAGES.unknown;

export const resolvePlatformBranding = (platform) => {
  const key = resolvePlatformBrandKey(
    platform && typeof platform === "object"
      ? platform.label ||
          platform.title ||
          platform.platform ||
          platform.platformName ||
          platform.name ||
          platform.channel ||
          platform.key
      : platform
  );
  if (!key) return null;
  return PLATFORM_BRANDING[key] || null;
};

/*
 * One platform -> visual contract, for any surface that needs to SHOW a
 * platform rather than colour a series: artwork, display label and the
 * canonical accent, resolved together from the identity normalization above.
 *
 * It composes the existing resolvers rather than introducing a second asset
 * map — `image` is always a bundled local asset (the hero map's own
 * `unknown` frame when nothing matches), so a consumer can render it
 * unconditionally and never paint a broken image or an alt-text placeholder.
 *
 * `accent` is the CANONICAL CATEGORICAL colour, not a brand colour: YouTube's
 * is the palette's blue, not YouTube red. Brand recognition belongs to the
 * artwork; the accent exists so a surface can tint its own chrome with a
 * governed hex instead of inventing one. It is an empty string for platforms
 * outside the categorical set, and a consumer must degrade to its own token
 * rather than substituting a colour.
 */
export const resolvePlatformVisual = (platform) => {
  const heroKey = resolvePlatformHeroKey(platform);
  /*
   * Walked KEY-FIRST, matching resolvePlatformHeroKey, rather than delegating
   * to resolvePlatformBranding — which reads `label` before `key`. A record
   * whose structured slug is "youtube" but whose manifest title is a catalog
   * name would otherwise take its artwork from the slug and its label and
   * accent from the title. One identity, resolved once, from the same order.
   */
  let branding = null;
  for (const candidate of getPlatformBrandInputCandidates(platform)) {
    branding = PLATFORM_BRANDING[resolvePlatformBrandKey(candidate)] || null;
    if (branding) break;
  }
  return Object.freeze({
    key: branding ? branding.key : heroKey,
    heroKey,
    image: PLATFORM_HERO_IMAGES[heroKey] || PLATFORM_HERO_IMAGES.unknown,
    label: branding ? branding.label : "",
    accent: (branding && branding.color) || "",
    known: heroKey !== "unknown",
  });
};

const isSherlocMetaFamilyInput = (value) => {
  const key = resolvePlatformBrandKey(value);
  if (["meta", "facebook", "instagram"].includes(key)) return true;
  const raw = String(value || "");
  const normalized = normalizePlatformBrandInput(raw);
  if (normalized === "meta") return true;
  return /\b(?:meta|facebook|instagram|fb|ig)\b/i.test(raw);
};

export const resolveSherlocPlatformBranding = (platform) => {
  if (getPlatformBrandInputCandidates(platform).some(isSherlocMetaFamilyInput)) {
    return PLATFORM_BRANDING.meta;
  }
  return resolvePlatformBranding(platform);
};

// Umbrella Meta entities name the parent brand rather than a specific child app:
// "Meta", "Meta (Facebook + Instagram)", "Meta (IG + FB)", "Meta Family",
// "Meta Platforms". resolvePlatformBrandKey already folds the combined
// Facebook+Instagram phrasings into the "meta" key; anything else whose primary
// token is "meta" (and therefore doesn't name a single child app — Facebook,
// Instagram, Messenger, Threads all start with their own name) is also umbrella.
const isMetaUmbrellaInput = (value) => {
  if (resolvePlatformBrandKey(value) === "meta") return true;
  return normalizePlatformBrandInput(value).startsWith("meta");
};

// Branding resolver for the public Campaign Strategy microsite. Unlike
// resolveSherlocPlatformBranding (used by the media-plan charts, where a grouped
// "Facebook" line represents the entire Meta channel and must show the Meta
// logo), the microsite renders individual Meta-owned platforms — Facebook,
// Instagram, Messenger, Threads — which must keep their own brand logo. Only the
// Meta umbrella entity collapses to the Meta infinity logo.
export const resolveMicrositePlatformBranding = (platform) => {
  const direct = resolvePlatformBranding(platform);
  if (direct) return direct;
  // Umbrella phrasings like "Meta Family" have no concrete branding key but
  // should still render the Meta logo.
  if (getPlatformBrandInputCandidates(platform).some(isMetaUmbrellaInput)) {
    return PLATFORM_BRANDING.meta;
  }
  return null;
};
